import lodashEscape from 'lodash.escape'

// Tag names arrive HTML-escaped from the API. Some names have been
// double-escaped server-side (e.g. `<tag>` stored as `&amp;lt;tag&amp;gt;`),
// so a single decode pass leaves the outer entities intact. Decode
// repeatedly until the output is stable (or a small iteration cap is
// hit) so the caller receives a fully decoded string regardless of how
// many layers of escaping the BE applied.
//
// Uses a detached <textarea> because textarea content is parsed as
// RCDATA — HTML entities decode, but decoded `<` characters are kept
// as text rather than being retokenized as tag-start markup.
const MAX_DECODE_PASSES = 5

// Reuse a single detached textarea across calls — this runs per tag
// during initial sync (potentially thousands of tags × up to 5 passes),
// and per-call allocation added avoidable GC pressure.
const decodeTextarea = document.createElement('textarea')

const decodeOnce = (str: string): string => {
  // Safe: the textarea is detached (never attached to the document) and
  // its content is read back via `.value`, which returns decoded text,
  // never executed. This is specifically the textarea RCDATA trick for
  // entity decoding — DOMParser was rejected because it retokenizes
  // decoded `<` as tag markup, which would strip HTML-shaped tag names.
  // eslint-disable-next-line no-unsanitized/property
  decodeTextarea.innerHTML = str
  return decodeTextarea.value
}

const unescapeTagName = (str = ''): string => {
  let current = str
  for (let i = 0; i < MAX_DECODE_PASSES; i++) {
    if (!current.includes('&')) return current
    const next = decodeOnce(current)
    if (next === current) return current
    current = next
  }
  return current
}

// Counterpart used when sending a tag name *to* the BE. The BE has an XSS
// sanitizer that silently strips `<…>` patterns from request bodies and
// query params, so a tag literally named `<new>` would never persist.
// Standard HTML escape (lodash escapes `&`, `<`, `>`, `"`, `'`) so the BE
// stores the safe form (`&lt;new&gt;`); unescapeTagName then restores the
// canonical text on every read path.
const escapeTagName = (str?: string | null): string =>
  typeof str === 'string' ? lodashEscape(str) : ''

export { unescapeTagName, escapeTagName }
